import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { InlineMessage } from 'transfix-ui/components/InlineMessage'
import { Spinner } from 'transfix-ui/components/Spinner'
import { Table, TableBody } from 'transfix-ui/components/Table'
import { Toast, useToastState } from 'transfix-ui/components/Toast'
import { TopNav } from 'transfix-ui/components/TopNav'
import { useValidationFormContext } from 'utils/context/ValidationFormContext'
import { DriversTableHeader } from 'apps/fleet-planner/components/drivers/DriversTableHeader'
import { DriversTableRow } from 'apps/fleet-planner/components/drivers/DriversTableRow'
import { DriverForm } from 'apps/fleet-planner/components/forms/DriverForm'
import { AddEntityModal } from 'apps/fleet-planner/components/modals/AddEntityModal'
import { FP_SUPPORT_EMAIL } from 'apps/fleet-planner/config'
import { useDriverApi, useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { useDriverParams } from 'apps/fleet-planner/hooks/useDriverParams'
import { createDriverRequest, DriverSchemaType } from 'apps/fleet-planner/schema'
import { DriverStatusType, IDriver } from 'apps/fleet-planner/types'
import { fetchDriversSuccess } from 'apps/fleet-planner/utils/actions/drivers'
import { TopNavCTA, Container } from 'components/layout'
import { getDriverById } from '../atoms'

export const getDefaultEditDriverState = (currentDriver?: Partial<IDriver>): DriverSchemaType => {
  if (currentDriver) {
    return {
      firstName: currentDriver?.firstName ?? '',
      lastName: currentDriver?.lastName ?? '',
      motorCarrier: {
        id: currentDriver?.motorCarrierId ?? 0,
        name: currentDriver?.motorCarrierLegalName ?? '',
        dotNumber: undefined,
      },
      email: currentDriver?.email ?? '',
      cellPhone: currentDriver?.cellPhone ?? '',
      status: currentDriver.status ?? DriverStatusType.active,
      settings: currentDriver.settings ?? {
        smsStatusUpdatesEnabled: false,
        suppressSmsStatusUpdatesOptIn: false,
      },
      cellPhoneIsTwilioSubscribed: currentDriver.cellPhoneIsTwilioSubscribed ?? true,
    }
  }
  return {}
}

export const DriversPage = () => {
  const { trackEvent } = useFMSAnalytics()
  const { params } = useDriverParams()
  const [currentDriver, setCurrentDriver] = useState<Partial<IDriver>>()
  const [lastUpdatedDriverId, setLastUpdatedDriverId] = useState<string | undefined>(undefined)
  const {
    createNewDriver,
    drivers,
    error: apiError,
    getDrivers,
    isDriverListLoading,
    isLoading,
    updateDriver,
  } = useDriverApi()
  const { closeToast, openToast, toastMessage, toastOpen, toastType } = useToastState()
  const {
    errors: driverFormErrors,
    setHasErrors: setHasDriverFormErrors,
  } = useValidationFormContext()
  const dispatch = useDispatch()
  const { pathname = '' } = useLocation()
  const history = useHistory()

  // Boolean flags to detect which modal is open
  const isEditModalOpen = pathname.includes('drivers/edit-driver') && !!currentDriver?.id
  const isAddModalOpen = pathname.includes('drivers/create-driver')

  // Fetch drivers when params are updated.
  useEffect(() => {
    if (!isDriverListLoading) {
      getDrivers(params)
    }
  }, [params, getDrivers])

  // If user accesses the page from a direct edit-driver URL, find and set the currentDriver
  useEffect(() => {
    if (pathname.includes('drivers/edit-driver')) {
      const driverId = pathname.split('/edit-driver/')[1]
      const currDriver = getDriverById(drivers, driverId)
      if (currDriver) setCurrentDriver(currDriver)
    }
  }, [drivers])

  // mixpanel tracking for viewing Manage Drivers page
  useEffect(() => {
    trackEvent('Driver Management', 'VIEWED_MANAGE_DRIVERS_PAGE')
  }, [])

  const clickAddDriverHandler = () => {
    history.push('/drivers/create-driver')
  }

  const cancelAddDriverHandler = () => {
    history.push('/drivers')
    setHasDriverFormErrors(false)
  }

  const clickEditDriverHandler = (currDriver: IDriver) => {
    history.push(`/drivers/edit-driver/${currDriver.id}`)
    setCurrentDriver(currDriver)
  }

  const cancelEditDriverHandler = () => {
    history.push('/drivers')
    setCurrentDriver(undefined)
    setHasDriverFormErrors(false)
  }

  const editDriverHandler = async (driverId: string, driver: Partial<IDriver>) => {
    await updateDriver(driverId, driver)
    if (!apiError) {
      setLastUpdatedDriverId(driverId)
    }
    // call getDrivers to remount the component and set drivers
    const response = await getDrivers(params)
    // update store with drivers list
    if (response) dispatch(fetchDriversSuccess(response))
    history.push('/drivers')
    setCurrentDriver(undefined)
  }

  const addNewDriverHandler = async (d: Partial<IDriver>) => {
    const lastDriver = await createNewDriver(d)
    trackEvent('Driver Management', 'ADD_DRIVER_MANAGE_DRIVERS')
    if (!apiError) {
      setLastUpdatedDriverId(lastDriver?.id)
    }
    // call getDrivers to remount the component and set drivers
    const response = await getDrivers(params)
    // update store with drivers list
    if (response) dispatch(fetchDriversSuccess(response))
    history.push('/drivers')
    openToast({
      toastType: 'default',
      toastMessage: 'Driver added.',
    })
  }

  // Create the driver request and send it
  const submitDriverHandler = async (driver: DriverSchemaType) => {
    const req = createDriverRequest(driver)
    // if currentDriver is set, then the request is for Edit Driver
    if (currentDriver?.id) {
      await editDriverHandler(currentDriver.id, req)
      trackEvent('Driver Management', 'EDIT_DRIVER_MANAGE_DRIVERS')
      openToast({
        toastType: 'default',
        toastMessage: 'Driver updated.',
      })
    } else {
      await addNewDriverHandler(req)
      openToast({
        toastType: 'default',
        toastMessage: 'Driver added.',
      })
    }
    // clear all form errors
    setHasDriverFormErrors(false)
  }

  // Upon changing the status from a table row, update the driver
  const submitStatusUpdateHandler = async (driver: DriverSchemaType) => {
    const req = createDriverRequest(driver)
    if (driver.id) {
      await editDriverHandler(driver.id, req)
      trackEvent('Driver Management', 'DEACTIVATE_DRIVER_MANAGE_DRIVERS')
      openToast({
        toastType: 'default',
        toastMessage: 'Status changed.',
      })
    }
  }

  const driversBulkUploadClickHandler = () =>
    trackEvent('Bulk Upload', 'CLICK_DRIVERS_BULK_UPLOAD_LINK')

  // If there's a newly updated Driver, push that driver to the top of the list
  const rearrangeDrivers = (driverList: IDriver[], id?: string): IDriver[] => {
    if (id) {
      const lastDriver = driverList.find(item => item.id === id)
      if (lastDriver) {
        const updatedDrivers = driverList.filter(item => item.id !== id)
        return [lastDriver, ...updatedDrivers]
      }
    }
    return driverList
  }
  const rearrangedDrivers = rearrangeDrivers(drivers, lastUpdatedDriverId)
  return (
    <>
      <TopNav title='Drivers'>
        <TopNavCTA label='Add Driver' onClick={clickAddDriverHandler} large />
      </TopNav>
      {isAddModalOpen && (
        <AddEntityModal
          open
          title='Add New Driver'
          error={apiError || driverFormErrors}
          onClose={cancelAddDriverHandler}
          dataTest='add-new-driver-modal'>
          <InlineMessage
            className='mb5'
            message={
              <span>
                <b>Lots of drivers to add?</b>{' '}
                <a href={`mailto:${FP_SUPPORT_EMAIL}`} onClick={driversBulkUploadClickHandler}>
                  Contact us
                </a>{' '}
                and we’ll import your drivers in bulk.
              </span>
            }
          />
          <DriverForm
            onSuccess={submitDriverHandler}
            onCancel={cancelAddDriverHandler}
            isLoading={isLoading}
          />
        </AddEntityModal>
      )}
      {isEditModalOpen && (
        <AddEntityModal
          open
          title='Edit Driver'
          error={apiError || driverFormErrors}
          onClose={cancelEditDriverHandler}
          dataTest='edit-driver-modal'>
          <DriverForm
            primaryButtonText='Save'
            defaultValues={getDefaultEditDriverState(currentDriver)}
            onSuccess={submitDriverHandler}
            onCancel={cancelEditDriverHandler}
            isLoading={isLoading}
            isEditMode
          />
        </AddEntityModal>
      )}
      <Container hidePadding>
        {isDriverListLoading && <Spinner position='relative' />}
        {!isDriverListLoading && (
          <Table>
            <DriversTableHeader params={params} onChange={() => {}} />
            <TableBody>
              {rearrangedDrivers.map((driver: IDriver) => (
                <DriversTableRow
                  key={driver?.id}
                  driver={driver}
                  isUpdated={driver.id === lastUpdatedDriverId}
                  onEditRow={clickEditDriverHandler}
                  onStatusUpdate={submitStatusUpdateHandler}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </Container>
      <Toast variation={toastType} open={toastOpen} onClose={closeToast} message={toastMessage} />
    </>
  )
}
